import React, { useState } from 'react';
import {
  // DatePicker,
  Spin,
  Button,
  Card,
  Row,
  Col,
  Collapse,
  Statistic,
  Space,
  Typography,
} from 'antd';
import {
  DollarOutlined,
  GoldOutlined,
  BankOutlined,
  RiseOutlined,
  FallOutlined,
  TeamOutlined,
  AlertOutlined,
  SafetyOutlined,
  TrophyOutlined,
  DownOutlined,
} from '@ant-design/icons';
// import dayjs from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import { getOurNewReportAction } from '../store/overviewActions';
import { type AppDispatch } from '../../../redux/store';
import fmt from 'indian-number-format';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Line,
  ComposedChart,
  // ReferenceLine,
} from 'recharts';

// const { RangePicker } = DatePicker;
const { Panel } = Collapse;
const { Title, Text } = Typography;

interface ReportData {
  branch: string;
  branchId: string;
  totalLoansDisbursedAmount: number;
  totalLoansDisbursedCount: number;
  totalLoanOutstanding: number;
  totalLoanRelease: number;
  goldInHandGross: number;
  goldInHandNet: number;
  goldStockValue: number;
  saleGoldInHandGross: number;
  saleGoldInHandNet: number;
  saleGoldStockValue: number;
  totalOb: number;
  obInterestPaid: number;
  kgfInterestReceived: number;
  totalNetCash: number;
  cashInHand: number;
  bankBalance: number;
  minimumBalance: number;
  pledgesUpForRenewalToday: number;
  pledgesUpForRenewalOthers: number;
  overdueAccounts_0_3: number;
  overdueAccounts_3_6: number;
  overdueAccounts_6_plus: number;
  overdueAmount_0_3: number;
  overdueAmount_3_6: number;
  overdueAmount_6_plus: number;
  totalActiveLoansCount: number;
  parClassification: string;
  netInterestMargin: number;
}

export default function PortfolioReport(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  // const dateFormat = 'YYYY-MM-DD';
  const [dataFetched, setDataFetched] = useState<boolean>(false);

  const { ourNewReport }: any = useSelector<any>((state) => state.overview);

  // const [startDate, setStartDate] = useState<string>(
  //   dayjs().format('YYYY-MM-DD')
  // );
  // const [endDate, setEndDate] = useState<string>(dayjs().format('YYYY-MM-DD'));

  // const onChangeDate = (values: any, formatString: [string, string]) => {
  //   setStartDate(formatString[0]);
  //   setEndDate(formatString[1]);
  // };

  const getReport = async (): Promise<void> => {
    setIsLoading(true);
    await getOurNewReportAction(dispatch, {
      // startDate,
      // endDate,
    });
    setIsLoading(false);
    setDataFetched(true);
  };

  const getTotalData = (): ReportData | null => {
    if (ourNewReport == null || ourNewReport.length === 0) return null;
    return (
      ourNewReport.find((item: ReportData) => item.branchId === 'total') ??
      ourNewReport[0]
    );
  };

  const getBranchData = (): ReportData[] => {
    if (ourNewReport == null || ourNewReport.length === 0) return [];
    return ourNewReport.filter((item: ReportData) => item.branchId !== 'total');
  };

  const totalData = getTotalData();
  const branchData = getBranchData();

  // Prepare overdue accounts data for bar chart
  const getOverdueChartData = () => {
    return branchData.map((branch) => ({
      name: branch.branch.replace(' Branch', ''),
      '0-3 Months': branch.overdueAccounts_0_3,
      '3-6 Months': branch.overdueAccounts_3_6,
      '6+ Months': branch.overdueAccounts_6_plus,
    }));
  };

  // Prepare overdue amounts data for bar chart
  const getOverdueAmountChartData = () => {
    return branchData.map((branch) => ({
      name: branch.branch.replace(' Branch', ''),
      '0-3 Months': branch.overdueAmount_0_3 / 100000,
      '3-6 Months': branch.overdueAmount_3_6 / 100000,
      '6+ Months': branch.overdueAmount_6_plus / 100000,
    }));
  };

  // Prepare gold distribution data
  const getGoldDistributionData = () => {
    return branchData.map((branch) => ({
      name: branch.branch.replace(' Branch', ''),
      'Gross Weight': branch.goldInHandGross,
      'Net Weight': branch.goldInHandNet,
      'Stock Value': branch.goldStockValue / 100000,
    }));
  };

  // Prepare cash distribution data for horizontal bar chart
  // const getCashDistributionData = () => {
  //   return branchData.map((branch) => ({
  //     name: branch.branch.replace(' Branch', ''),
  //     'Cash in Hand': branch.cashInHand,
  //     'Bank Balance': branch.bankBalance,
  //   }));
  // };

  // Summary card component
  const SummaryCard = ({
    title,
    value,
    icon,
    suffix = '',
    prefix = '₹',
    color = '#1890ff',
    branches,
    shouldRoundOff,
  }: {
    title: string;
    value: number;
    icon: React.ReactNode;
    suffix?: string;
    prefix?: string;
    color?: string;
    branches?: Array<{ branch: string; value: number; count?: number }>;
    shouldRoundOff?: boolean;
  }) => {
    const formattedValue =
      prefix === '₹' || shouldRoundOff === true
        ? fmt.format(Math.round(value))
        : value.toFixed(2);

    const panelHeader = (
      <div className="summary-card-header">
        <div
          className="summary-card-icon"
          style={{ background: `${color}15`, color }}
        >
          {icon}
        </div>
        <div className="summary-card-content">
          <Text className="summary-card-title">{title}</Text>
          <Title level={3} className="summary-card-value" style={{ color }}>
            {prefix}
            {formattedValue}
            {suffix}
          </Title>
        </div>
        {branches != null && (
          <DownOutlined style={{ fontSize: 12, color: '#8c8c8c' }} />
        )}
      </div>
    );

    if (branches == null) {
      return (
        <Card className="summary-card" hoverable>
          {panelHeader}
        </Card>
      );
    }

    return (
      <Collapse ghost className="summary-card-collapse" expandIcon={() => null}>
        <Panel header={panelHeader} key="1" className="summary-card-panel">
          <div className="branch-details">
            {branches.map((branch, index) => (
              <div key={index} className="branch-detail-item">
                <Text className="branch-name">{branch.branch}</Text>
                <Space>
                  <Text strong style={{ color }}>
                    {prefix}
                    {prefix === '₹' || shouldRoundOff === true
                      ? fmt.format(Math.round(branch.value))
                      : branch.value.toFixed(2)}
                    {suffix}
                  </Text>
                  {branch.count !== undefined && (
                    <Text type="secondary">({fmt.format(branch.count)})</Text>
                  )}
                </Space>
              </div>
            ))}
          </div>
        </Panel>
      </Collapse>
    );
  };

  return (
    <div className="portfolio-report">
      <Spin spinning={isLoading} fullscreen />

      <div className="portfolio-report__search">
        {/* <RangePicker
          className="rangepicker"
          onChange={onChangeDate}
          defaultValue={[dayjs(startDate), dayjs(endDate)]}
          format={dateFormat}
        /> */}
        <Button
          onClick={async () => {
            await getReport();
          }}
          className="report__button"
        >
          <span>GetReport</span>
        </Button>
      </div>

      {dataFetched && totalData != null && (
        <div className="report-content" style={{ overflow: 'hidden' }}>
          {/* Key Metrics Cards */}
          <div className="section-header">
            <Title level={4}>Key Metrics</Title>
            <Text type="secondary">Overview of portfolio performance</Text>
          </div>

          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} lg={8}>
              <SummaryCard
                title="Total Loans Disbursed"
                value={totalData.totalLoansDisbursedAmount}
                icon={<DollarOutlined />}
                color="#DD1E2D"
                branches={branchData.map((b) => ({
                  branch: b.branch,
                  value: b.totalLoansDisbursedAmount,
                  count: b.totalLoansDisbursedCount,
                }))}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <SummaryCard
                title="Interest Received"
                value={totalData.kgfInterestReceived}
                icon={<TrophyOutlined />}
                color="#8B0000"
                branches={branchData.map((b) => ({
                  branch: b.branch,
                  value: b.kgfInterestReceived,
                }))}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <SummaryCard
                title="OB Interest Paid"
                value={totalData.obInterestPaid}
                icon={<DollarOutlined />}
                color="#ff7a45"
                branches={branchData.map((b) => ({
                  branch: b.branch,
                  value: b.obInterestPaid,
                }))}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <SummaryCard
                title="Total Loan Release"
                value={totalData.totalLoanRelease}
                icon={<FallOutlined />}
                color="#595959"
                branches={branchData.map((b) => ({
                  branch: b.branch,
                  value: b.totalLoanRelease,
                }))}
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12} lg={8}>
              <SummaryCard
                title="Total Net Cash"
                value={totalData.totalNetCash}
                icon={<BankOutlined />}
                color="#DD1E2D"
                branches={branchData.map((b) => ({
                  branch: b.branch,
                  value: b.totalNetCash,
                }))}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <SummaryCard
                title="Bank Balance"
                value={totalData.bankBalance}
                icon={<BankOutlined />}
                color="#1890ff"
                branches={branchData.map((b) => ({
                  branch: b.branch,
                  value: b.bankBalance,
                }))}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <SummaryCard
                title="Cash In Hand"
                value={totalData.cashInHand}
                icon={<DollarOutlined />}
                color="#52c41a"
                branches={branchData.map((b) => ({
                  branch: b.branch,
                  value: b.cashInHand,
                }))}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <SummaryCard
                title="H.O"
                value={totalData.minimumBalance}
                icon={<SafetyOutlined />}
                color="#722ed1"
                branches={branchData.map((b) => ({
                  branch: b.branch,
                  value: b.minimumBalance,
                }))}
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12} lg={8}>
              <SummaryCard
                title="Gold Stock Value"
                value={totalData.goldStockValue}
                icon={<GoldOutlined />}
                color="#D4AF37"
                branches={branchData.map((b) => ({
                  branch: b.branch,
                  value: b.goldStockValue,
                }))}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <SummaryCard
                title="Gold In Hand (Gross)"
                value={totalData.goldInHandGross}
                icon={<GoldOutlined />}
                color="#FFD700"
                suffix=" gm"
                prefix=""
                branches={branchData.map((b) => ({
                  branch: b.branch,
                  value: b.goldInHandGross,
                }))}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <SummaryCard
                title="Gold In Hand (Net)"
                value={totalData.goldInHandNet}
                icon={<GoldOutlined />}
                color="#D4AF37"
                suffix=" gm"
                prefix=""
                branches={branchData.map((b) => ({
                  branch: b.branch,
                  value: b.goldInHandNet,
                }))}
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12} lg={8}>
              <SummaryCard
                title="Total Active Loans"
                value={Math.round(totalData.totalActiveLoansCount)}
                icon={<TeamOutlined />}
                color="#8B0000"
                prefix=""
                shouldRoundOff={true}
                branches={branchData.map((b) => ({
                  branch: b.branch,
                  value: Math.round(b.totalActiveLoansCount),
                }))}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <SummaryCard
                title="Total OB Outstanding"
                value={totalData.totalOb}
                icon={<SafetyOutlined />}
                color="#DD1E2D"
                branches={branchData.map((b) => ({
                  branch: b.branch,
                  value: b.totalOb,
                }))}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <SummaryCard
                title="Total Loan Outstanding"
                value={totalData.totalLoanOutstanding}
                icon={<RiseOutlined />}
                color="#8B0000"
                branches={branchData.map((b) => ({
                  branch: b.branch,
                  value: b.totalLoanOutstanding,
                }))}
              />
            </Col>
          </Row>

          <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
            <Col xs={24} sm={12} lg={8}>
              <SummaryCard
                title="Sale Gold Stock Value"
                value={totalData.saleGoldStockValue ?? 0}
                icon={<GoldOutlined />}
                color="#B8860B"
                branches={branchData.map((b) => ({
                  branch: b.branch,
                  value: b.saleGoldStockValue ?? 0,
                }))}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <SummaryCard
                title="Sale Gold In Hand (Gross)"
                value={totalData.saleGoldInHandGross ?? 0}
                icon={<GoldOutlined />}
                color="#FFD700"
                suffix=" gm"
                prefix=""
                branches={branchData.map((b) => ({
                  branch: b.branch,
                  value: b.saleGoldInHandGross ?? 0,
                }))}
              />
            </Col>
            <Col xs={24} sm={12} lg={8}>
              <SummaryCard
                title="Sale Gold In Hand (Net)"
                value={totalData.saleGoldInHandNet ?? 0}
                icon={<GoldOutlined />}
                color="#D4AF37"
                suffix=" gm"
                prefix=""
                branches={branchData.map((b) => ({
                  branch: b.branch,
                  value: b.saleGoldInHandNet ?? 0,
                }))}
              />
            </Col>
          </Row>

          {/* Charts Section */}
          <div className="section-header" style={{ marginTop: 40 }}>
            <Title level={4}>Analytics</Title>
            <Text type="secondary">
              Visual insights into portfolio performance
            </Text>
          </div>

          <Row gutter={[24, 24]} style={{ marginTop: 16 }}>
            {/* Gold Distribution Chart */}
            <Col xs={24}>
              <Card className="chart-card">
                <Title level={5}>
                  <GoldOutlined /> Gold Distribution Across Branches
                </Title>
                <Text
                  type="secondary"
                  style={{ display: 'block', marginBottom: 20 }}
                >
                  Comparative analysis of gold holdings (Stock value in Lakhs)
                </Text>
                <ResponsiveContainer width="100%" height={300}>
                  <ComposedChart data={getGoldDistributionData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis yAxisId="left" />
                    <YAxis yAxisId="right" orientation="right" />
                    <Tooltip />
                    <Legend />
                    <Bar
                      yAxisId="left"
                      dataKey="Gross Weight"
                      fill="#D4AF37"
                      name="Gross Weight (gm)"
                    />
                    <Bar
                      yAxisId="left"
                      dataKey="Net Weight"
                      fill="#B8860B"
                      name="Net Weight (gm)"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="Stock Value"
                      stroke="#DD1E2D"
                      strokeWidth={2}
                      name="Stock Value (₹ Lakhs)"
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </Card>
            </Col>

            {/* Cash Distribution Bar Chart */}
            {/* <Col xs={24} lg={12}>
              <Card className="chart-card">
                <Title level={5}>
                  <BankOutlined /> Cash Distribution
                </Title>
                <Text
                  type="secondary"
                  style={{ display: 'block', marginBottom: 20 }}
                >
                  Cash in hand vs bank balance by branch
                </Text>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={getCashDistributionData()}
                    layout="vertical"
                    margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      type="number"
                      domain={['auto', 'auto']}
                      tickFormatter={(value: number) =>
                        fmt.format(Math.round(value))
                      }
                    />
                    <YAxis dataKey="name" type="category" width={100} />
                    <Tooltip
                      formatter={(value: number) =>
                        `₹${fmt.format(Math.round(value))}`
                      }
                    />
                    <Legend />
                    <ReferenceLine x={0} stroke="#000" strokeWidth={2} />
                    <Bar dataKey="Cash in Hand" fill="#DD1E2D" />
                    <Bar dataKey="Bank Balance" fill="#8B0000" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col> */}
          </Row>

          {/* Overdue Accounts Analysis */}
          <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
            <Col xs={24}>
              <Card className="chart-card">
                <Title level={5}>
                  <AlertOutlined /> Overdue Accounts Analysis
                </Title>
                <Text
                  type="secondary"
                  style={{ display: 'block', marginBottom: 20 }}
                >
                  Branch-wise distribution of overdue accounts
                </Text>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={getOverdueChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="0-3 Months" fill="#FFA8A8" />
                    <Bar dataKey="3-6 Months" fill="#DD1E2D" />
                    <Bar dataKey="6+ Months" fill="#8B0000" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>

          {/* Overdue Amounts Analysis */}
          <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
            <Col xs={24}>
              <Card className="chart-card">
                <Title level={5}>
                  <AlertOutlined /> Overdue Amounts Analysis
                </Title>
                <Text
                  type="secondary"
                  style={{ display: 'block', marginBottom: 20 }}
                >
                  Branch-wise distribution of overdue amounts (₹ Lakhs)
                </Text>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart data={getOverdueAmountChartData()}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis
                      tickFormatter={(value: number) =>
                        fmt.format(Math.round(value))
                      }
                      label={{
                        value: '₹ Lakhs',
                        angle: -90,
                        position: 'insideLeft',
                      }}
                    />
                    <Tooltip
                      formatter={(value: number) =>
                        `₹${fmt.format(value.toFixed(2))} Lakhs`
                      }
                    />
                    <Legend />
                    <Bar dataKey="0-3 Months" fill="#FFA8A8" />
                    <Bar dataKey="3-6 Months" fill="#DD1E2D" />
                    <Bar dataKey="6+ Months" fill="#8B0000" />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            </Col>
          </Row>

          {/* Renewal Alerts */}
          <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
            <Col xs={24} md={12}>
              <Card className="alert-card today-renewal">
                <Space
                  direction="vertical"
                  size="small"
                  style={{ width: '100%' }}
                >
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <AlertOutlined style={{ fontSize: 24, color: '#DD1E2D' }} />
                    <Title level={5} style={{ margin: 0 }}>
                      Pledges Up for Renewal Today
                    </Title>
                  </div>
                  <Statistic
                    value={totalData.pledgesUpForRenewalToday}
                    valueStyle={{ color: '#DD1E2D', fontSize: 32 }}
                  />
                  <Text type="secondary">Requires immediate attention</Text>
                </Space>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card className="alert-card upcoming-renewal">
                <Space
                  direction="vertical"
                  size="small"
                  style={{ width: '100%' }}
                >
                  <div
                    style={{ display: 'flex', alignItems: 'center', gap: 8 }}
                  >
                    <AlertOutlined style={{ fontSize: 24, color: '#595959' }} />
                    <Title level={5} style={{ margin: 0 }}>
                      Upcoming Renewals
                    </Title>
                  </div>
                  <Statistic
                    value={totalData.pledgesUpForRenewalOthers}
                    valueStyle={{ color: '#595959', fontSize: 32 }}
                  />
                  <Text type="secondary">Plan ahead for renewals</Text>
                </Space>
              </Card>
            </Col>
          </Row>

          {/* Performance Indicators */}
          <div style={{ marginTop: 24 }}>
            <Title level={5}>Branch Performance Indicators</Title>
            <div
              className="performance-grid"
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'wrap',
                gap: 16,
                marginTop: 16,
              }}
            >
              {branchData.map((branch, index) => (
                <Card
                  key={index}
                  size="small"
                  className="performance-card"
                  style={{
                    width: '300px',
                    borderLeft: `4px solid ${
                      branch.parClassification === 'good'
                        ? '#52c41a'
                        : '#DD1E2D'
                    }`,
                  }}
                >
                  <Space
                    direction="vertical"
                    size="small"
                    style={{ width: '100%' }}
                  >
                    <Text strong>{branch.branch}</Text>
                    <Space split="|">
                      <div>
                        <Text type="secondary">PAR: </Text>
                        <Text
                          strong
                          style={{
                            color:
                              branch.parClassification === 'good'
                                ? '#52c41a'
                                : '#DD1E2D',
                          }}
                        >
                          {branch.parClassification.toUpperCase()}
                        </Text>
                      </div>
                      <div>
                        <Text type="secondary">NIM: </Text>
                        <Text strong>
                          {(branch.netInterestMargin * 100).toFixed(2)}%
                        </Text>
                      </div>
                      <div>
                        <Text type="secondary">Active Loans: </Text>
                        <Text strong>
                          {fmt.format(branch.totalActiveLoansCount)}
                        </Text>
                      </div>
                    </Space>
                  </Space>
                </Card>
              ))}
            </div>
          </div>
        </div>
      )}

      {!dataFetched && (
        <div className="empty-state">
          <Space direction="vertical" size="large">
            <GoldOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />
            <div>
              <Title level={4}>No Data Available</Title>
              <Text type="secondary">
                Select a date range and generate report to view analytics
              </Text>
            </div>
          </Space>
        </div>
      )}
    </div>
  );
}
